/**
 * Regional API Service
 * Handles all regional targeting related API calls
 */

import api from '../helpers/axios';
import { 
  State, 
  TargetScope, 
  RegionalPriority, 
  StateLanguage,
  LanguageDTO,
  RegionalTargetingPayload 
} from '../types/regionalTypes';

export const regionalApiService = {
  // Get all active states
  getStates: async (): Promise<State[]> => {
    try {
      let allStates: State[] = [];
      let page = 0;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get(`/api/states?page=${page}&size=50`);
        
        // Handle paginated response - extract content array
        if (response.data && Array.isArray(response.data.content)) {
          allStates = [...allStates, ...response.data.content];
          hasMore = !response.data.last;
          page++;
        } else if (Array.isArray(response.data)) {
          // Handle direct array response (fallback)
          allStates = response.data;
          hasMore = false;
        } else {
          console.error('States API returned unexpected format:', response.data);
          hasMore = false;
        }
      }
      
      return allStates;
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  },

  // Get target scopes
  getTargetScopes: async (): Promise<TargetScope[]> => {
    try {
      const response = await api.get('/api/regional/target-scopes');
      return response.data;
    } catch (error) {
      console.error('Error fetching target scopes:', error);
      throw error;
    }
  },

  // Get regional priorities
  getRegionalPriorities: async (): Promise<RegionalPriority[]> => {
    try {
      const response = await api.get('/api/regional/priorities');
      return response.data;
    } catch (error) {
      console.error('Error fetching regional priorities:', error);
      throw error;
    }
  },

  // Get languages for a specific state
  getStateLanguages: async (stateId: number): Promise<StateLanguage[]> => {
    try {
      const response = await api.get(`/api/regional/states/${stateId}/languages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching state languages:', error);
      throw error;
    }
  },

  // Get active districts for a state (for dependent dropdown)
  getDistrictsByState: async (stateId: number): Promise<{ id: number; displayName: string; code?: string }[]> => {
    try {
      const response = await api.get(`/api/reporter/geo/states/${stateId}/districts`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching districts for state', stateId, error);
      throw error;
    }
  },

  // Get primary language for a state
  getPrimaryLanguage: async (stateId: number): Promise<StateLanguage | null> => {
    try {
      const response = await api.get(`/api/regional/states/${stateId}/primary-language`);
      return response.data;
    } catch (error) {
      console.error('Error fetching primary language:', error);
      return null;
    }
  },

  // Get articles by state (for mobile and filtering)
  getArticlesByState: async (stateId: number): Promise<any[]> => {
    try {
      const response = await api.get(`/api/articles/state/${stateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching articles by state:', error);
      throw error;
    }
  },

  // Get content recommendations based on regional preferences
  getRecommendations: async (primaryStateId: number, secondaryStateIds?: number[]): Promise<any[]> => {
    try {
      const params = new URLSearchParams({
        primaryState: primaryStateId.toString(),
        ...(secondaryStateIds && { secondaryStates: secondaryStateIds.join(',') })
      });
      
      const response = await api.get(`/api/articles/recommendations?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  },

  // Get unique primary languages from all states
  getPrimaryLanguages: async (): Promise<LanguageDTO[]> => {
    try {
      const response = await api.get('/api/regional/languages/primary');
      return response.data;
    } catch (error) {
      console.error('Error fetching primary languages:', error);
      throw error;
    }
  },

  // Get all active unique languages
  getAllLanguages: async (): Promise<LanguageDTO[]> => {
    try {
      const response = await api.get('/api/regional/languages');
      return response.data;
    } catch (error) {
      console.error('Error fetching all languages:', error);
      throw error;
    }
  }
};

export default regionalApiService;
